import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TemplateLineItem {
  description: string;
  unit: string;
  sub_line_types: ("labor" | "material")[];
}

export interface EstimateTemplate {
  id: string;
  company_id: string;
  name: string;
  category: string;
  line_items: TemplateLineItem[];
  labor_markup_pct: number;
  material_markup_pct: number;
  created_at: string;
  updated_at: string;
}

export function useEstimateTemplates() {
  return useQuery<EstimateTemplate[]>({
    queryKey: ["estimate_templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimate_templates")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((d: any) => ({
        ...d,
        line_items: (d.line_items ?? []) as TemplateLineItem[],
      }));
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<EstimateTemplate, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("estimate_templates")
        .insert({
          company_id: input.company_id,
          name: input.name,
          category: input.category,
          line_items: input.line_items as any,
          labor_markup_pct: input.labor_markup_pct,
          material_markup_pct: input.material_markup_pct,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_templates"] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<EstimateTemplate> & { id: string }) => {
      const updateObj: Record<string, any> = {};
      if (input.name !== undefined) updateObj.name = input.name;
      if (input.category !== undefined) updateObj.category = input.category;
      if (input.line_items !== undefined) updateObj.line_items = input.line_items as any;
      if (input.labor_markup_pct !== undefined) updateObj.labor_markup_pct = input.labor_markup_pct;
      if (input.material_markup_pct !== undefined) updateObj.material_markup_pct = input.material_markup_pct;
      const { data, error } = await supabase
        .from("estimate_templates")
        .update(updateObj)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_templates"] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("estimate_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_templates"] }),
  });
}
